'use client';

import { Button } from '~/components/primitives';
import Title from '~/components/utils/Title';
import { useI18n } from '~/features/i18n';
import { InputSelect } from '~/components/primitives';
import { std } from '@funkey/shared/utils';
import Image from 'next/image';
import LOGIN_BACKGROUND_IMAGE from '~/../public/images/loginBackground.jpg';
import { useStateObject } from '~/utils/react';
import { useMemo, useRef } from 'react';
import { cx } from 'class-variance-authority';
import BizzLayout from '../../_components/BizzLayout';
import { parseAsInteger, useQueryState } from 'nuqs';

export const BizzDashboardWelcomeText: React.FC<{
    title: React.ReactNode;
    content: React.ReactNode;
    image: {
        src: string;
        alt: string;
    } | null;
}> = props => {
    const i18n = useI18n();

    const contentRef = useRef<HTMLDivElement>(null);
    const isOpen = useStateObject(false);

    return (
        <BizzLayout.Section className="relative flex flex-col-reverse items-start justify-between gap-6 md:flex-row md:gap-10">
            <div className="flex max-w-4xl flex-col gap-6">
                {props.title && <Title>{props.title}</Title>}
                {props.content && (
                    <div
                        className="prose relative overflow-hidden transition-all ease-in-out"
                        style={{
                            maxHeight: (isOpen.value && contentRef.current?.clientHeight) || 16 * 9,
                        }}
                    >
                        <div ref={contentRef}>{props.content}</div>

                        <div
                            aria-hidden
                            className={cx(
                                'absolute bottom-0 left-0 h-10 w-full transition-all ease-in',
                                isOpen.value === true ? 'opacity-0' : 'opacity-100',
                            )}
                            style={{
                                backgroundImage: 'linear-gradient(transparent, var(--bizz-bg))',
                            }}
                        />
                    </div>
                )}
                <Button
                    variant="link"
                    className="p-0! w-fit text-secondary transition hover:text-turqoise-900"
                    onClick={() => isOpen.set(prev => !prev)}
                >
                    {isOpen.value ? i18n._('Read less') : i18n._('Read more')}
                </Button>
            </div>

            {/* eslint-disable-next-line jsx-a11y/alt-text */}
            <Image
                className={cx('shrink-0 rounded-xl', isOpen.value && 'md:sticky md:top-28')}
                src={props.image?.src ?? LOGIN_BACKGROUND_IMAGE.src}
                alt={props.image?.alt ?? 'Welcome text image'}
                width={362}
                height={200}
            />
        </BizzLayout.Section>
    );
};

export const BizzYearSelect: React.FC<{ bizzCreatedAt: Date }> & {
    useState: typeof useYearSelectState;
} = props => {
    const i18n = useI18n();
    const [year, setYear, currentYear] = useYearSelectState();

    return (
        <InputSelect
            label={i18n._('Year')}
            className="w-28"
            value={year.toString()}
            options={std.Array.range(props.bizzCreatedAt.getFullYear(), currentYear + 1)
                .reverse()
                .map(x => ({
                    value: x.toString(),
                    label: x.toString(),
                }))}
            onChange={yearStr => {
                const yearNum = typeof yearStr === 'number' ? yearStr : parseInt(yearStr, 10);

                if (Number.isNaN(yearNum)) throw new Error(`Invalid year "${yearStr}"`);

                setYear(yearNum);
            }}
        />
    );
};

function useYearSelectState() {
    const currentYear = useMemo(() => new Date().getFullYear(), []);
    const [state, setState] = useQueryState(
        'year',
        parseAsInteger.withOptions({ shallow: false }).withDefault(currentYear),
    );

    return [state, setState, currentYear] as const;
}

BizzYearSelect.useState = useYearSelectState;
